/* Bootstrap dell'app multi-diagramma: login → routing tra dashboard (catalogo
   dei diagrammi per cliente) e vista/editor di un singolo diagramma (?d=<id>). */
import "./generated/diagram.css";
import "./ui.css";
import shellHtml from "./generated/shell.html?raw";
import { api, type User } from "./api.js";
import { App } from "./app.js";
import { attachEditor } from "./editor/editor.js";
import { mountDashboard } from "./ui/dashboard.js";
import { showLogin } from "./ui/login.js";
import { mountBadge } from "./ui/badge.js";
import { applyTheme, savedTheme } from "./ui/theme.js";
import { buildAppBar } from "./ui/appbar.js";

const root = document.querySelector("#app") as HTMLElement;
let currentUser: User | null = null;

function currentDiagramId(): number | null {
  const p = new URLSearchParams(location.search).get("d");
  const n = p ? Number(p) : NaN;
  return Number.isFinite(n) && n > 0 ? n : null;
}

function setDiagramId(id: number | null): void {
  const url = new URL(location.href);
  if (id == null) url.searchParams.delete("d");
  else url.searchParams.set("d", String(id));
  history.pushState({}, "", url);
}

function showError(e: unknown): void {
  root.textContent = "Errore: " + (e instanceof Error ? e.message : String(e));
  // eslint-disable-next-line no-console
  console.error(e);
}

async function openDiagram(user: User, id: number): Promise<void> {
  const state = await api.getDiagram(id);
  if (!state.doc) {
    root.textContent = "Diagramma senza contenuto.";
    return;
  }
  root.innerHTML = shellHtml;
  // stessa barra della home, con lo slot dove l'editor mette "← Diagrammi"
  root.prepend(buildAppBar({ user, withBackSlot: true }));
  // il guscio ha già il suo interruttore del tema: nell'app lo governa la barra,
  // due bottoni identici a due righe di distanza confondono e basta. Nell'HTML
  // esportato il guscio è da solo e il suo bottone resta.
  const shellTheme = root.querySelector<HTMLElement>("#theme");
  if (shellTheme) shellTheme.hidden = true;
  // il titolo nell'intestazione riflette il diagramma corrente (la shell ha un
  // testo statico "Flusso urgenze"); si preserva il <small> descrittivo.
  const h1 = root.querySelector("#bar h1");
  if (h1?.firstChild) h1.firstChild.nodeValue = state.title + " ";
  document.title = `${state.title} · Fullsteps`;
  const app = new App(root, state.doc);
  app.init();
  attachEditor(app, root, {
    user,
    diagramId: id,
    baseVersionId: state.version?.id ?? null,
    share: state.share,
    lock: state.lock,
    onBack: () => {
      setDiagramId(null);
      route(user);
    },
  });
}

function route(user: User): void {
  const id = currentDiagramId();
  if (id != null) {
    void openDiagram(user, id).catch((e) => {
      setDiagramId(null);
      showError(e);
    });
  } else {
    document.title = "Fullsteps";
    mountDashboard(root, user, (openId) => {
      setDiagramId(openId);
      void openDiagram(user, openId).catch(showError);
    });
  }
}

async function boot(): Promise<void> {
  applyTheme(savedTheme());
  mountBadge();
  let user = await api.me();
  if (!user) user = await showLogin(root);
  currentUser = user;
  addEventListener("popstate", () => {
    if (currentUser) route(currentUser);
  });
  route(user);
}

boot().catch(showError);
